import {jsPDF} from "jspdf";
import {Model} from "@/app/Model";
import type {PenaleDashboardFlow} from "@/app/data/PenaleDashboardFlow";
import type {PenalePercorsoFlow} from "@/app/data/PenalePercorsoFlow";
import type {PenaleData} from "@/app/data/PenaleData";
import type {KeyValoreStr, KeyValoreStrNum} from "@/app/data/DataTypes";
import type {FamigliaDashboardFlow} from "@/app/data/FamigliaDashboardFlow";
import type {FamigliaPercorsoFlow} from "@/app/data/FamigliaPercorsoFlow";
import type {FamigliaData} from "@/app/data/FamigliaData";
import type {ResponsabilitaDashboardFlow} from "@/app/data/ResponsabilitaDashboardFlow";
import type {ResponsabilitaPercorsoFlow} from "@/app/data/ResponsabilitaPercorsoFlow";
import type {ResponsabilitaData} from "@/app/data/ResponsabilitaData";

declare let frontendConfig: any;

export class PdfGenerator {
    static inDebug: boolean = true;
    static generating: boolean = false;

    //templates penale
    static templatePenale_Tuttiassolti: string = `{{CAPO_IMPUTAZIONE}}  {{IMPORTI_CONTESTATI}}
     <br>
    Ti sei soffermato sulle sentenze che hanno portato all'assoluzione dell’imputato.<br> 
    {{MOTIVO_ASSOLUZIONE}}
    
    {{FILTRI_APPLICATI1}}
    {{DURATA_PROCESSO}}
    
    {{FILTRI_ULTERIORI}}
    <br>
    Hai ottenuto {{NUMERO_DOCUMENTI}} che {{SODDISFANO}} tutti i criteri selezionati e i cui estremi {{ESTREMI_SELEZIONATE}} sono:
    <br>
    `;
    static templatePenale_Nonassolti: string = `{{CAPO_IMPUTAZIONE}} {{IMPORTI_CONTESTATI}}
    <br>
    Ti sei soffermato sulle sentenze che hanno portato alla condanna dell’imputato. <br>
    {{SANZIONE_APPLICABILE}}
    
    {{FILTRI_APPLICATI2}}
    {{DURATA_PROCESSO}}
    {{DURATA_CONDANNA}}
    {{IMPORTI_EVASI}}
    {{IMPORTI_RISARCITI}}
    
    {{FILTRI_ULTERIORI}}
    {{NON_MENZIONE}}
    <br>
    Hai ottenuto {{NUMERO_DOCUMENTI}} che {{SODDISFANO}} tutti i criteri selezionati e i cui estremi {{ESTREMI_SELEZIONATE}} sono:
    <br>
    `;
    static templatePenale_Mista: string = `{{CAPO_IMPUTAZIONE}} {{IMPORTI_CONTESTATI}}
    <br>
    Hai scelto di esplorare entrambi i percorsi, assoluzione e condanna, in un insieme misto.<br>
    
    {{MOTIVI_ASSOLUZIONE}}
    {{TIPO_SANZIONE}}
    
    {{FILTRI_APPLICATI3}}
    {{DURATA_PROCESSO}}
    {{DURATA_CONDANNA}}
    {{IMPORTI_EVASI}}
    {{IMPORTI_RISARCITI}}
    
    {{FILTRI_ULTERIORI}}
    {{NON_MENZIONE}}
    <br>
    Hai ottenuto {{NUMERO_DOCUMENTI}} che {{SODDISFANO}} tutti i criteri selezionati e i cui estremi {{ESTREMI_SELEZIONATE}} sono:
    <br>
    `;

    //templates famiglia
    static templateFamiglia_Comune: string = `{{OGGETTO_DI_GIUDIZIO}}.<br>
    {{TIPOLOGIA_PROCEDIMENTO}}
    {{ADDEBITO}}
    {{PERCORSO_FIGLI}}
    
    {{DURATA_PROCESSO}}
    {{FILTRI_ULTERIORI}}
    <br>
    Hai ottenuto {{NUMERO_DOCUMENTI}} che {{SODDISFANO}} tutti i criteri selezionati e i cui estremi {{ESTREMI_SELEZIONATE}} sono:
    <br>`;

    //templates responsabilita
    static templateResponsabilita_Comune: string = `{{TIPOLOGIA_DANNO}}.<br>
    {{TIPOLOGIA_DANNO_PATRIMONIALE}}
    {{TIPOLOGIA_DANNO_NON_PATRIMONIALE}}
    
    {{DOMANDA_ACCOLTA}}
    {{PERCORSO_VALORI}}
    
    {{DURATA_PROCEDIMENTO}}
    {{FILTRI_ULTERIORI}}
    <br>
    Hai ottenuto {{NUMERO_DOCUMENTI}} che {{SODDISFANO}} tutti i criteri selezionati e i cui estremi {{ESTREMI_SELEZIONATE}} sono:
    <br>`;

    //templates common
    static templateFooter: string = `(C)2024-2025 Giuffrè Francis Lefebvre S.p.A.`;

    //generation penale
    static pen_generatePdfDashboard(what: string = "penale") {
        let model: Model = Model.getReactiveInstance();
        if (!model.generatingPdf) {
            let now: Date = (new Date());
            model.generatingPdf = true;
            let unique: string = now.toISOString().replace(/ /g, "").replace(/:/g, "-");
            let where: string = "";
            let useFlow: PenaleDashboardFlow;
            let useData: PenaleData;
            let useTemplate: string = "";
            switch (what) {
                case "penale":
                    useFlow = model.pen_flow_dashboard;
                    useData = model.pen_get_dashboard;
                    switch (useFlow.assoluzione) {
                        case "Tutti assolti":
                            useTemplate = PdfGenerator.templatePenale_Tuttiassolti;
                            where = "_assoluzione";
                            break;
                        case "Non assolti":
                            useTemplate = PdfGenerator.templatePenale_Nonassolti;
                            where = "_condanna";
                            break;
                        case "Mista":
                            useTemplate = PdfGenerator.templatePenale_Mista;
                            where = "_mista";
                            break;
                    }
                    break;
            }
            let fileName: string = "strategia_" + what + where + "_" + unique + ".pdf";
            const {
                capo_imputazione_legge,
                importo_evaso_active,
                importo_evaso_da,
                importo_evaso_a,
                assoluzione_motivo,
                pena_principale,
                non_menzione_nel_casellario,
                durata_pena_active,
                durata_del_processo,
                durata_del_processo_notNull,
                durata_pena_notNull,
                id_localita,
                giudice,
                importo_risarcimento_active
            } = model.pen_flow_dashboard;

            let NUMERO_DOCUMENTI: string = model.pen_get_dashboard.totale_numero_sentenze.valore.toString() + (model.pen_get_dashboard.totale_numero_sentenze.valore > 1? " documenti": " documento");
            let SODDISFANO: string = 'soddisfa';
            if (model.pen_get_dashboard.totale_numero_sentenze.valore > 1) SODDISFANO = 'soddisfano';
            useTemplate = useTemplate.replace('{{SODDISFANO}}', SODDISFANO);
            let ESTREMI_SELEZIONATE: string = "";
            if (model.pen_get_dashboard.totale_numero_sentenze.valore > model.pen_get_dashboard.sentenze_paginate.length) ESTREMI_SELEZIONATE = ` dei ${model.pen_get_dashboard.sentenze_paginate.length} selezionati `
            let rubrica: string = "";
            let rubItem:KeyValoreStr | undefined = model.lista_capo_imputazione_legge.find( (x: KeyValoreStr): boolean => {
                return x.capo_imputazione_legge === capo_imputazione_legge;
            } );
            if (rubItem && rubItem.rubrica && rubItem.rubrica != capo_imputazione_legge) rubrica = " : " + rubItem.rubrica;
            let CAPO_IMPUTAZIONE: string = capo_imputazione_legge != ""? `Hai scelto di analizzare le cause relative al capo di imputazione ${capo_imputazione_legge} ${rubrica}` : '';
            useTemplate = useTemplate.replace('{{NUMERO_DOCUMENTI}}', NUMERO_DOCUMENTI).replace('{{CAPO_IMPUTAZIONE}}', CAPO_IMPUTAZIONE).replace('{{ESTREMI_SELEZIONATE}}', ESTREMI_SELEZIONATE);

            let IMPORTI_CONTESTATI: string = importo_evaso_active ?
                ` selezionando un range di importi contestati che va da € ${importo_evaso_da} a € ${importo_evaso_a}` : ''; //TODO non sono quelli evasi?
            let MOTIVO_ASSOLUZIONE: string = assoluzione_motivo !== '' ?
                `Questa selezione ha portato alla scelta del motivo di assoluzione ${assoluzione_motivo}<br>` : '';
            let SANZIONE_APPLICABILE: string = pena_principale !== '' ?
                `Questa selezione ha portato alla scelta del tipo di sanzioni applicabile della ${pena_principale} <br>` : '';
            let NON_MENZIONE: string = non_menzione_nel_casellario ?
                `&bull; infine è stato inserito il filtro della non menzione nel casellario giudiziale. <br>` : '';
            useTemplate = useTemplate.replace('{{IMPORTI_CONTESTATI}}', IMPORTI_CONTESTATI).replace('{{MOTIVO_ASSOLUZIONE}}', MOTIVO_ASSOLUZIONE).replace('{{SANZIONE_APPLICABILE}}', SANZIONE_APPLICABILE).replace('{{NON_MENZIONE}}', NON_MENZIONE)

            let DURATA_PROCESSO: string = (durata_del_processo || durata_del_processo_notNull) ?
                `&bull; sono stati selezionati i procedimenti in cui è possibile risalire alla durata del processo;<br>` : '';
            let DURATA_CONDANNA: string = (durata_pena_active || durata_pena_notNull) ?
                `&bull; sono stati selezionati i procedimenti in cui è possibile risalire alla durata della condanna;<br>` : '';
            useTemplate = useTemplate.replace('{{DURATA_PROCESSO}}', DURATA_PROCESSO).replace('{{DURATA_PROCESSO}}', DURATA_PROCESSO).replace('{{DURATA_CONDANNA}}', DURATA_CONDANNA);

            let FILTRI_ULTERIORI: string = '';
            if (id_localita != '' || giudice != '') {
                FILTRI_ULTERIORI = `Sono stati applicati ulteriori filtri, quali:<br>`;
                let nomeCitta: string = id_localita;
                let findCitta: KeyValoreStr | undefined = model.pen_lista_citta.find((x: KeyValoreStr): boolean => { return x.id_localita == id_localita });
                if (findCitta) nomeCitta = findCitta.citta;
                if (id_localita != '') FILTRI_ULTERIORI += `&bull; il luogo di pronuncia ${nomeCitta}<br>`;
                if (giudice != '') FILTRI_ULTERIORI += `&bull; il Giudice della decisione ${giudice}<br>`;
            }
            useTemplate = useTemplate.replace('{{FILTRI_ULTERIORI}}', FILTRI_ULTERIORI);

            let IMPORTI_EVASI: string = importo_evaso_active ?
                `&bull; è stato individuato un range di importi evasi o non corrisposti che va da € ${importo_evaso_da} a € ${importo_evaso_a} <br>` : '';
            let IMPORTI_RISARCITI: string = importo_risarcimento_active ?
                `&bull; sono stati selezionati i procedimenti in cui vi è stato un risarcimento da parte dei condannati <br>` : '';
            useTemplate = useTemplate.replace('{{IMPORTI_EVASI}}', IMPORTI_EVASI).replace('{{IMPORTI_RISARCITI}}', IMPORTI_RISARCITI);

            let MOTIVI_ASSOLUZIONE: string = assoluzione_motivo != "" ?
                `Ti sei soffermato sulle sentenze che hanno portato all'assoluzione dell’imputato. <br>
Questa selezione ha porta alla scelta del motivo di assoluzione  ${assoluzione_motivo}<br>` : '';
            let TIPO_SANZIONE: string = pena_principale != "" ?
                `Ti sei soffermato sulle sentenze che hanno portato alla condanna dell’imputato.  <br>
Questa selezione ha portato alla scelta del tipo di sanzioni applicabile della ${pena_principale} <br>` : '';
            useTemplate = useTemplate.replace('{{MOTIVI_ASSOLUZIONE}}', MOTIVI_ASSOLUZIONE).replace('{{TIPO_SANZIONE}}', TIPO_SANZIONE);

            let someFilter1: boolean = false;
            let someFilter2: boolean = false;
            let someFilter3: boolean = false;
            if (DURATA_PROCESSO != "") {
                someFilter1 = true;
                someFilter2 = true;
                someFilter3 = true;
            }
            if (DURATA_CONDANNA != "" || IMPORTI_EVASI != "" || IMPORTI_RISARCITI != "") {
                someFilter2 = true;
                someFilter3 = true;
            }
            let FILTRI_APPLICATI1: string = someFilter1? `Dopo aver esplorato le ragioni di assoluzione, hai optato per applicare i seguenti filtri:<br>`: '';
            let FILTRI_APPLICATI2: string = someFilter2? `Dopo aver esplorato i tipi di punizioni, hai optato per applicare i seguenti filtri:<br>`: '';
            let FILTRI_APPLICATI3: string = someFilter3? `Hai optato per applicare i seguenti filtri:<br>`: '';
            useTemplate = useTemplate.replace('{{FILTRI_APPLICATI1}}', FILTRI_APPLICATI1).replace('{{FILTRI_APPLICATI2}}', FILTRI_APPLICATI2).replace('{{FILTRI_APPLICATI3}}', FILTRI_APPLICATI3)

            let varLogo: string = "/img/logo.png";
            if (frontendConfig.rebranding) {
                varLogo = "/img/LOGO_STRATEGIA.png";
            }
            
            PdfGenerator.getImage(varLogo).then((image: HTMLImageElement): void => {
                let divHtmlToPdf: HTMLElement = document.createElement("div");
                divHtmlToPdf.style.width = "720px";
                divHtmlToPdf.style.maxWidth = "720px";
                divHtmlToPdf.style.fontSize = "12px";
                divHtmlToPdf.style.fontFamily = "Arial";
                divHtmlToPdf.style.letterSpacing = "0.05em";
                image.style.height = "50px";
                image.style.width = "auto";
                image.style.float = "left";
                image.style.marginRight = "20px";
                divHtmlToPdf.append(image)
                let contentDiv: HTMLElement = document.createElement("div");
                divHtmlToPdf.append(contentDiv)
                contentDiv.innerHTML = '<div style="font-size: 18px; color:var(--gfl-blue); padding: 27px 0 10px 70px">Report del ' + now.toLocaleDateString() + ' ' + now.toLocaleTimeString() + '</div><div><br></div><div>' + useTemplate +
                    '</div><div>' + PdfGenerator.getEstremi(useData.sentenze_paginate) + '</div><div><br></div><div style="font-size: 9px; color:#999999">' + PdfGenerator.templateFooter + '</div>';

                var doc = new jsPDF({
                        orientation: 'p',
                        unit: 'px',
                        format: 'a4',
                        putOnlyUsedFonts: true,
                        floatPrecision: 16,
                        hotfixes: ["px_scaling"]
                    }
                );
                doc.html(divHtmlToPdf, {
                    callback: function (doc) {
                        doc.save(fileName);
                        model.generatingPdf = false;
                    },
                    x: 30,
                    y: 30
                });
            });
        } else {
            alert("Prego attendere!")
        }

    }
    static pen_generatePdfPercorso(what: string = "penale") {
        let model: Model = Model.getReactiveInstance();
        if (!model.generatingPdf) {
            let now: Date = (new Date());
            model.generatingPdf = true;
            let unique: string = now.toISOString().replace(/ /g, "").replace(/:/g, "-");
            let where: string = "";
            let useFlow: PenalePercorsoFlow;
            let useData: PenaleData;
            let useTemplate: string = "";
            switch (what) {
                case "penale":
                    useFlow = model.pen_flow_percorso;
                    useData = model.pen_get_percorso;
                    switch (useFlow.assoluzione) {
                        case "Tutti assolti":
                            useTemplate = PdfGenerator.templatePenale_Tuttiassolti;
                            where = "_assoluzione";
                            break;
                        case "Non assolti":
                            useTemplate = PdfGenerator.templatePenale_Nonassolti;
                            where = "_condanna";
                            break;
                        case "Mista":
                            useTemplate = PdfGenerator.templatePenale_Mista;
                            where = "_mista";
                            break;
                    }
                    break;
            }
            let fileName: string = "strategia_" + what + where + "_" + unique + ".pdf";
            const {
                capo_imputazione_legge,
                importo_evaso_active,
                importo_evaso_da,
                importo_evaso_a,
                assoluzione_motivo,
                pena_principale
            } = model.pen_flow_percorso;

            let NUMERO_DOCUMENTI: string = model.pen_get_percorso.totale_numero_sentenze.valore.toString() + (model.pen_get_percorso.totale_numero_sentenze.valore > 1? " documenti": " documento");
            let SODDISFANO: string = 'soddisfa';
            if (model.pen_get_percorso.totale_numero_sentenze.valore > 1) SODDISFANO = 'soddisfano';
            useTemplate = useTemplate.replace('{{SODDISFANO}}', SODDISFANO);
            let ESTREMI_SELEZIONATE: string = "";
            if (model.pen_get_percorso.totale_numero_sentenze.valore > model.pen_get_percorso.sentenze_paginate.length) ESTREMI_SELEZIONATE = ` delle ${model.pen_get_percorso.sentenze_paginate.length} selezionate `
            let rubrica: string = "";
            let rubItem:KeyValoreStr | undefined = model.lista_capo_imputazione_legge.find( (x: KeyValoreStr): boolean => {
                return x.capo_imputazione_legge === capo_imputazione_legge;
            } );
            if (rubItem!= undefined && rubItem.rubrica && rubItem.rubrica != capo_imputazione_legge) rubrica = " : " + rubItem.rubrica;
            let CAPO_IMPUTAZIONE: string = capo_imputazione_legge != ""? `Hai scelto di analizzare le cause relative al capo di imputazione ${capo_imputazione_legge} ${rubrica}` : '';
            useTemplate = useTemplate.replace('{{NUMERO_DOCUMENTI}}', NUMERO_DOCUMENTI).replace('{{CAPO_IMPUTAZIONE}}', CAPO_IMPUTAZIONE).replace('{{ESTREMI_SELEZIONATE}}', ESTREMI_SELEZIONATE);

            let IMPORTI_CONTESTATI: string = importo_evaso_active ?
                ` selezionando un range di importi contestati che va da € ${importo_evaso_da} a € ${importo_evaso_a}` : ''; //TODO non sono quelli evasi?
            let MOTIVO_ASSOLUZIONE: string = assoluzione_motivo !== '' ?
                `Questa selezione ha portato alla scelta del motivo di assoluzione ${assoluzione_motivo}<br>` : '';
            let SANZIONE_APPLICABILE: string = pena_principale !== '' ?
                `Questa selezione ha portato alla scelta del tipo di sanzioni applicabile della ${pena_principale} <br>` : '';
            let NON_MENZIONE: string = '';
            useTemplate = useTemplate.replace('{{IMPORTI_CONTESTATI}}', IMPORTI_CONTESTATI).replace('{{MOTIVO_ASSOLUZIONE}}', MOTIVO_ASSOLUZIONE).replace('{{SANZIONE_APPLICABILE}}', SANZIONE_APPLICABILE).replace('{{NON_MENZIONE}}', NON_MENZIONE)

            let DURATA_PROCESSO: string = '';
            let DURATA_CONDANNA: string = '';
            useTemplate = useTemplate.replace('{{DURATA_PROCESSO}}', DURATA_PROCESSO).replace('{{DURATA_PROCESSO}}', DURATA_PROCESSO).replace('{{DURATA_CONDANNA}}', DURATA_CONDANNA);

            let FILTRI_ULTERIORI: string = '';
            useTemplate = useTemplate.replace('{{FILTRI_ULTERIORI}}', FILTRI_ULTERIORI);

            let IMPORTI_EVASI: string = importo_evaso_active ?
                `&bull; è stato individuato un range di importi evasi o non corrisposti che va da € ${importo_evaso_da} a € ${importo_evaso_a} <br>` : '';
            let IMPORTI_RISARCITI: string = '';
            useTemplate = useTemplate.replace('{{IMPORTI_EVASI}}', IMPORTI_EVASI).replace('{{IMPORTI_RISARCITI}}', IMPORTI_RISARCITI);

            let MOTIVI_ASSOLUZIONE: string = assoluzione_motivo != "" ?
                `Ti sei soffermato sulle sentenze che hanno portato all'assoluzione dell’imputato. <br>
Questa selezione ha porta alla scelta del motivo di assoluzione  ${assoluzione_motivo}<br>` : '';
            let TIPO_SANZIONE: string = pena_principale != "" ?
                `Ti sei soffermato sulle sentenze che hanno portato alla condanna dell’imputato.  <br>
Questa selezione ha portato alla scelta del tipo di sanzioni applicabile della ${pena_principale} <br>` : '';
            useTemplate = useTemplate.replace('{{MOTIVI_ASSOLUZIONE}}', MOTIVI_ASSOLUZIONE).replace('{{TIPO_SANZIONE}}', TIPO_SANZIONE);

            let someFilter1: boolean = false;
            let someFilter2: boolean = false;
            let someFilter3: boolean = false;
            if (DURATA_PROCESSO != "") {
                someFilter1 = true;
                someFilter2 = true;
                someFilter3 = true;
            }
            if (DURATA_CONDANNA != "" || IMPORTI_EVASI != "" || IMPORTI_RISARCITI != "") {
                someFilter2 = true;
                someFilter3 = true;
            }
            let FILTRI_APPLICATI1: string = someFilter1? `Dopo aver esplorato le ragioni di assoluzione, hai optato per applicare i seguenti filtri:<br>`: '';
            let FILTRI_APPLICATI2: string = someFilter2? `Dopo aver esplorato i tipi di punizioni, hai optato per applicare i seguenti filtri:<br>`: '';
            let FILTRI_APPLICATI3: string = someFilter3? `Hai optato per applicare i seguenti filtri:<br>`: '';
            useTemplate = useTemplate.replace('{{FILTRI_APPLICATI1}}', FILTRI_APPLICATI1).replace('{{FILTRI_APPLICATI2}}', FILTRI_APPLICATI2).replace('{{FILTRI_APPLICATI3}}', FILTRI_APPLICATI3)

            let varLogo: string = "/img/logo.png";
            if (frontendConfig.rebranding) {
                varLogo = "/img/LOGO_STRATEGIA.png";
            }
            
            PdfGenerator.getImage(varLogo).then((image: HTMLImageElement): void => {
                let divHtmlToPdf: HTMLElement = document.createElement("div");
                divHtmlToPdf.style.width = "720px";
                divHtmlToPdf.style.maxWidth = "720px";
                divHtmlToPdf.style.fontSize = "12px";
                divHtmlToPdf.style.fontFamily = "Arial";
                divHtmlToPdf.style.letterSpacing = "0.05em";
                image.style.height = "50px";
                image.style.width = "auto";
                image.style.float = "left";
                image.style.marginRight = "20px";
                divHtmlToPdf.append(image)
                let contentDiv: HTMLElement = document.createElement("div");
                divHtmlToPdf.append(contentDiv)
                contentDiv.innerHTML = '<div style="font-size: 18px; color:var(--gfl-blue); padding: 27px 0 10px 70px">Report del ' + now.toLocaleDateString() + ' ' + now.toLocaleTimeString() + '</div><div><br></div><div>' + useTemplate +
                    '</div><div>' + PdfGenerator.getEstremi(useData.sentenze_paginate) + '</div><div><br></div><div style="font-size: 9px; color:#999999">' + PdfGenerator.templateFooter + '</div>';

                var doc = new jsPDF({
                        orientation: 'p',
                        unit: 'px',
                        format: 'a4',
                        putOnlyUsedFonts: true,
                        floatPrecision: 16,
                        hotfixes: ["px_scaling"]
                    }
                );
                doc.html(divHtmlToPdf, {
                    callback: function (doc) {
                        doc.save(fileName);
                        model.generatingPdf = false;
                    },
                    x: 30,
                    y: 30
                });
            });
        } else {
            alert("Prego attendere!")
        }

    }

    //generation famiglia
    static fam_generatePdfDashboard(what: string = "famiglia") {
        let model: Model = Model.getReactiveInstance();
        if (!model.generatingPdf) {
            let now: Date = (new Date());
            model.generatingPdf = true;
            let unique: string = now.toISOString().replace(/ /g, "").replace(/:/g, "-");
            let where: string = "";
            let useFlow: FamigliaDashboardFlow;
            let useData: FamigliaData;
            let useTemplate: string = PdfGenerator.templateFamiglia_Comune;
            switch (what) {
                case "famiglia":
                    useFlow = model.fam_flow_dashboard;
                    useData = model.fam_get_dashboard;
                    switch (useFlow.oggetto_di_giudizio) {
                        case "Separazione":
                            where = "_separazione";
                            break;
                        case "Divorzio":
                            where = "_divorzio";
                            break;
                    }
                    break;
            }
            let fileName: string = "strategia_" + what + where + "_" + unique + ".pdf";

            //---
            const {
                oggetto_di_giudizio,
                tipologia_procedimento,
                addebito,
                motivi_addebito_separazione,
                regime_affidamento,
                regime_collocamento,
                regime_mantenimento,
                durata_del_processo ,
                durata_del_processo_notNull,
                assegnazione_casa_familiare,
                valore_assegno_divorzile_active,
                valore_assegno_divorzile_da,
                valore_assegno_divorzile_a,
                valore_assegno_mantenimento_active,
                valore_assegno_mantenimento_da,
                valore_assegno_mantenimento_a,
                valore_assegno_contributo_mantenimento_active,
                valore_assegno_contributo_mantenimento_da,
                valore_assegno_contributo_mantenimento_a,
                id_localita,
                giudice
            } = model.fam_flow_dashboard;

            let OGGETTO_DI_GIUDIZIO: string = oggetto_di_giudizio != "" ? "Hai scelto di analizzare le cause relative al procedimento di  " + oggetto_di_giudizio : '.<br>';
            useTemplate = useTemplate.replace('{{OGGETTO_DI_GIUDIZIO}}', OGGETTO_DI_GIUDIZIO);

            let TIPOLOGIA_PROCEDIMENTO: string = tipologia_procedimento != "" ? "Ti sei quindi soffermato sulle controversie di natura " + tipologia_procedimento + "<br>":"";
            let ADDEBITO: string = addebito != "" ? addebito == "Si"? "Hai individuato sentenze in cui vi è addebito della separazione": "Hai individuato sentenze in cui non vi è addebito della separazione" :"";
            if (ADDEBITO != "") {
                if (addebito == "Si") {
                    ADDEBITO += motivi_addebito_separazione != "" ? ", optando per questi motivi: " + motivi_addebito_separazione + ".<br>" : ".<br>";
                } else {
                    ADDEBITO +=".<br>";
                }
            }
            useTemplate = useTemplate.replace('{{TIPOLOGIA_PROCEDIMENTO}}', TIPOLOGIA_PROCEDIMENTO).replace('{{ADDEBITO}}', ADDEBITO)

            let NUMERO_DOCUMENTI: string = model.fam_get_dashboard.totale_numero_sentenze.valore.toString() + (model.fam_get_dashboard.totale_numero_sentenze.valore > 1? " documenti": " documento");
            let SODDISFANO: string = 'soddisfa';
            if (model.fam_get_dashboard.totale_numero_sentenze.valore > 1) SODDISFANO = 'soddisfano';
            useTemplate = useTemplate.replace('{{SODDISFANO}}', SODDISFANO);
            let ESTREMI_SELEZIONATE: string = "";
            if (model.fam_get_dashboard.totale_numero_sentenze.valore > model.fam_get_dashboard.sentenze_paginate.length) ESTREMI_SELEZIONATE = ` dei ${model.fam_get_dashboard.sentenze_paginate.length} selezionati `
            useTemplate = useTemplate.replace('{{NUMERO_DOCUMENTI}}', NUMERO_DOCUMENTI).replace('{{ESTREMI_SELEZIONATE}}', ESTREMI_SELEZIONATE);

            let PERCORSO_FIGLI: string = '';
            if (regime_affidamento!= "" ||  regime_collocamento!= "" ||  regime_mantenimento!= "" ) {
                let inMerito: string = "Hai optato per applicare i filtri relativi agli aspetti legati ai figli.<br> Sono stati selezionati i procedimenti in cui si è disposto in merito a:<br>";
                if (regime_affidamento!= "") {
                    PERCORSO_FIGLI += inMerito + "- Regime di affidamento, con applicazione della seguente tipologia di affidamento: " + regime_affidamento + ";<br>";
                    inMerito = "";
                }
                if (regime_collocamento!= "") {
                    PERCORSO_FIGLI += inMerito + "- Regime di collocamento, con applicazione della seguente tipologia di collocamento: " + regime_collocamento + ";<br>";
                    inMerito = "";
                }
                if (regime_mantenimento!= "") {
                    PERCORSO_FIGLI += inMerito + "- Regime di mantenimento, con applicazione della seguente tipologia di mantenimento: " + regime_mantenimento + ";<br>";
                    inMerito = "";
                }
                PERCORSO_FIGLI += "<br>";
            }
            useTemplate = useTemplate.replace('{{PERCORSO_FIGLI}}', PERCORSO_FIGLI);

            let DURATA_PROCESSO: string = (durata_del_processo || durata_del_processo_notNull) ?
                `Sono stati selezionati i procedimenti in cui è possibile risalire alla durata del processo;<br>` : '';
            useTemplate = useTemplate.replace('{{DURATA_PROCESSO}}', DURATA_PROCESSO);

            let FILTRI_ULTERIORI: string = '';
            let INNER_FILTRI_ULTERIORI: string = '';
            if (assegnazione_casa_familiare != '') {
                INNER_FILTRI_ULTERIORI += `&bull; assegnazione casa coniugale a ${assegnazione_casa_familiare};<br>`;
            }
            if (valore_assegno_divorzile_active) {
                INNER_FILTRI_ULTERIORI += `&bull; è stato individuato un range per il valore dell'assegno di divorzio che va da € ${valore_assegno_divorzile_da} a € ${valore_assegno_divorzile_a};<br>`;
            }
            if (valore_assegno_mantenimento_active) {
                INNER_FILTRI_ULTERIORI += `&bull; è stato individuato un range per il valore dell'assegno di mantenimento che va da € ${valore_assegno_mantenimento_da} a € ${valore_assegno_mantenimento_a};<br>`;
            }
            if (valore_assegno_contributo_mantenimento_active) {
                INNER_FILTRI_ULTERIORI += `&bull; è stato individuato un range per il valore dell'assegno del contributo al mantenimento che va da € ${valore_assegno_contributo_mantenimento_da} a € ${valore_assegno_contributo_mantenimento_a};<br>`;
            }
            if (id_localita != '') {
                let nomeCitta: string = id_localita;
                let findCitta: KeyValoreStr | undefined = model.fam_lista_citta.find((x: KeyValoreStr): boolean => {
                    return x.id_localita == id_localita
                });
                if (findCitta) nomeCitta = findCitta.citta;
                INNER_FILTRI_ULTERIORI += `&bull; il luogo di pronuncia ${nomeCitta};<br>`;
            }
            if (giudice != '') {
                INNER_FILTRI_ULTERIORI += `&bull; il Giudice della decisione ${giudice};<br>`;
            }
            if (INNER_FILTRI_ULTERIORI != "") {
                FILTRI_ULTERIORI += "<br>Sono stati applicati ulteriori filtri, quali:<br>";
                FILTRI_ULTERIORI += INNER_FILTRI_ULTERIORI;
                FILTRI_ULTERIORI += "<br>";
            }
            useTemplate = useTemplate.replace('{{FILTRI_ULTERIORI}}', FILTRI_ULTERIORI);

            let varLogo: string = "/img/logo.png";
            if (frontendConfig.rebranding) {
                varLogo = "/img/LOGO_STRATEGIA.png";
            }
            
            PdfGenerator.getImage(varLogo).then((image: HTMLImageElement): void => {
                let divHtmlToPdf: HTMLElement = document.createElement("div");
                divHtmlToPdf.style.width = "720px";
                divHtmlToPdf.style.maxWidth = "720px";
                divHtmlToPdf.style.fontSize = "12px";
                divHtmlToPdf.style.fontFamily = "Arial";
                divHtmlToPdf.style.letterSpacing = "0.05em";
                image.style.height = "50px";
                image.style.width = "auto";
                image.style.float = "left";
                image.style.marginRight = "20px";
                divHtmlToPdf.append(image)
                let contentDiv: HTMLElement = document.createElement("div");
                divHtmlToPdf.append(contentDiv)
                contentDiv.innerHTML = '<div style="font-size: 18px; color:var(--gfl-blue); padding: 27px 0 10px 70px">Report del ' + now.toLocaleDateString() + ' ' + now.toLocaleTimeString() + '</div><div><br></div><div>' + useTemplate +
                    '</div><div>' + PdfGenerator.getEstremi(useData.sentenze_paginate) + '</div><div><br></div><div style="font-size: 9px; color:#999999">' + PdfGenerator.templateFooter + '</div>';
                var doc = new jsPDF({
                        orientation: 'p',
                        unit: 'px',
                        format: 'a4',
                        putOnlyUsedFonts: true,
                        floatPrecision: 16,
                        hotfixes: ["px_scaling"]
                    }
                );
                doc.html(divHtmlToPdf, {
                    callback: function (doc) {
                        doc.save(fileName);
                        model.generatingPdf = false;
                    },
                    x: 30,
                    y: 30
                });
            });
        } else {
            alert("Prego attendere!")
        }
    }
    static fam_generatePdfPercorso(what: string = "famiglia") {
        let model: Model = Model.getReactiveInstance();
        if (!model.generatingPdf) {
            let now: Date = (new Date());
            model.generatingPdf = true;
            let unique: string = now.toISOString().replace(/ /g, "").replace(/:/g, "-");
            let where: string = "";
            let useFlow: FamigliaPercorsoFlow;
            let useData: FamigliaData;
            let useTemplate: string = PdfGenerator.templateFamiglia_Comune;
            switch (what) {
                case "famiglia":
                    useFlow = model.fam_flow_percorso;
                    useData = model.fam_get_percorso;
                    switch (useFlow.oggetto_di_giudizio) {
                        case "Separazione":
                            where = "_separazione";
                            break;
                        case "Divorzio":
                            where = "_divorzio";
                            break;
                    }
                    break;
            }
            let fileName: string = "strategia_" + what + where + "_" + unique + ".pdf";

            //---
            const {
                oggetto_di_giudizio,
                tipologia_procedimento,
                addebito,
                motivi_addebito_separazione,
                figli,
                regime_affidamento,
                regime_collocamento,
                regime_mantenimento
            } = model.fam_flow_percorso;

            let OGGETTO_DI_GIUDIZIO: string = oggetto_di_giudizio != "" ? "Hai scelto di analizzare le cause relative al procedimento di  " + oggetto_di_giudizio : '.<br>';
            useTemplate = useTemplate.replace('{{OGGETTO_DI_GIUDIZIO}}', OGGETTO_DI_GIUDIZIO);

            let TIPOLOGIA_PROCEDIMENTO: string = tipologia_procedimento != "" ? "Ti sei quindi soffermato sulle controversie di natura " + tipologia_procedimento + "<br>":"";
            let ADDEBITO: string = addebito != "" ? addebito == "Si"? "Hai individuato sentenze in cui vi è addebito della separazione": "Hai individuato sentenze in cui non vi è addebito della separazione" :"";
            if (ADDEBITO != "") {
                if (addebito == "Si") {
                    ADDEBITO += motivi_addebito_separazione != "" ? ", optando per questi motivi: " + motivi_addebito_separazione + ".<br>" : ".<br>";
                } else {
                    ADDEBITO +=".<br>";
                }
            }
            useTemplate = useTemplate.replace('{{TIPOLOGIA_PROCEDIMENTO}}', TIPOLOGIA_PROCEDIMENTO).replace('{{ADDEBITO}}', ADDEBITO)

            let NUMERO_DOCUMENTI: string = model.fam_get_percorso.totale_numero_sentenze.valore.toString() + (model.fam_get_percorso.totale_numero_sentenze.valore > 1? " documenti": " documento");
            let SODDISFANO: string = 'soddisfa';
            if (model.fam_get_percorso.totale_numero_sentenze.valore > 1) SODDISFANO = 'soddisfano';
            useTemplate = useTemplate.replace('{{SODDISFANO}}', SODDISFANO);
            let ESTREMI_SELEZIONATE: string = "";
            if (model.fam_get_percorso.totale_numero_sentenze.valore > model.fam_get_percorso.sentenze_paginate.length) ESTREMI_SELEZIONATE = ` dei ${model.fam_get_percorso.sentenze_paginate.length} selezionati `
            useTemplate = useTemplate.replace('{{NUMERO_DOCUMENTI}}', NUMERO_DOCUMENTI).replace('{{ESTREMI_SELEZIONATE}}', ESTREMI_SELEZIONATE);

            let PERCORSO_FIGLI: string = '';
            if (figli || regime_affidamento!= "" ||  regime_collocamento!= "" ||  regime_mantenimento!= "" ) {
                let inMerito: string = "Hai optato per applicare i filtri relativi agli aspetti legati ai figli.<br> Sono stati selezionati i procedimenti in cui si è disposto in merito a:<br>";
                if (regime_affidamento!= "") {
                    PERCORSO_FIGLI += inMerito + "- Regime di affidamento, con applicazione della seguente tipologia di affidamento: " + regime_affidamento + ";<br>";
                    inMerito = "";
                }
                if (regime_collocamento!= "") {
                    PERCORSO_FIGLI += inMerito + "- Regime di collocamento, con applicazione della seguente tipologia di collocamento: " + regime_collocamento + ";<br>";
                    inMerito = "";
                }
                if (regime_mantenimento!= "") {
                    PERCORSO_FIGLI += inMerito + "- Regime di mantenimento, con applicazione della seguente tipologia di mantenimento: " + regime_mantenimento + ";<br>";
                    inMerito = "";
                }
                PERCORSO_FIGLI += "<br>";
            }
            useTemplate = useTemplate.replace('{{PERCORSO_FIGLI}}', PERCORSO_FIGLI);

            let DURATA_PROCESSO: string = '';
            useTemplate = useTemplate.replace('{{DURATA_PROCESSO}}', DURATA_PROCESSO);

            let FILTRI_ULTERIORI: string = '';
            useTemplate = useTemplate.replace('{{FILTRI_ULTERIORI}}', FILTRI_ULTERIORI);

            let varLogo: string = "/img/logo.png";
            if (frontendConfig.rebranding) {
                varLogo = "/img/LOGO_STRATEGIA.png";
            }
            
            PdfGenerator.getImage(varLogo).then((image: HTMLImageElement): void => {
                let divHtmlToPdf: HTMLElement = document.createElement("div");
                divHtmlToPdf.style.width = "720px";
                divHtmlToPdf.style.maxWidth = "720px";
                divHtmlToPdf.style.fontSize = "12px";
                divHtmlToPdf.style.fontFamily = "Arial";
                divHtmlToPdf.style.letterSpacing = "0.05em";
                image.style.height = "50px";
                image.style.width = "auto";
                image.style.float = "left";
                image.style.marginRight = "20px";
                divHtmlToPdf.append(image)
                let contentDiv: HTMLElement = document.createElement("div");
                divHtmlToPdf.append(contentDiv)
                contentDiv.innerHTML = '<div style="font-size: 18px; color:var(--gfl-blue); padding: 27px 0 10px 70px">Report del ' + now.toLocaleDateString() + ' ' + now.toLocaleTimeString() + '</div><div><br></div><div>' + useTemplate +
                    '</div><div>' + PdfGenerator.getEstremi(useData.sentenze_paginate) + '</div><div><br></div><div style="font-size: 9px; color:#999999">' + PdfGenerator.templateFooter + '</div>';
                var doc = new jsPDF({
                        orientation: 'p',
                        unit: 'px',
                        format: 'a4',
                        putOnlyUsedFonts: true,
                        floatPrecision: 16,
                        hotfixes: ["px_scaling"]
                    }
                );
                doc.html(divHtmlToPdf, {
                    callback: function (doc) {
                        doc.save(fileName);
                        model.generatingPdf = false;
                    },
                    x: 30,
                    y: 30
                });
            });
        } else {
            alert("Prego attendere!")
        }
    }

    //generation responsabilita
    static res_generatePdfDashboard(what: string = "responsabilita") {
        let model: Model = Model.getReactiveInstance();
        if (!model.generatingPdf) {
            let now: Date = (new Date());
            model.generatingPdf = true;
            let unique: string = now.toISOString().replace(/ /g, "").replace(/:/g, "-");
            let where: string = "";
            let useFlow: ResponsabilitaDashboardFlow;
            let useData: ResponsabilitaData;
            let useTemplate: string = PdfGenerator.templateResponsabilita_Comune;
            switch (what) {
                case "responsabilita":
                    useFlow = model.res_flow_dashboard;
                    useData = model.res_get_dashboard;
                    switch (useFlow.tipologia_danno) {
                        case "Patrimoniale":
                            where = "_patrimoniale";
                            break;
                        case "Non patrimoniale":
                            where = "_non_patrimoniale";
                            break;
                        case "Entrambi":
                            where = "_entrambi";
                            break;
                    }
                    break;
            }
            let fileName: string = "strategia_" + what + where + "_" + unique + ".pdf";

            //---
            const {
                durata_procedimento_active,
                durata_procedimento,
                durata_procedimento_notNull,
                tipologia_danno,
                tipologia_danno_patrimoniale,
                tipologia_danno_non_patrimoniale,
                risarcimento_richiesto_active,
                risarcimento_richiesto_da,
                risarcimento_richiesto_a ,
                risarcimento_disposto_totale_active,
                risarcimento_disposto_totale_da,
                risarcimento_disposto_totale_a ,
                domanda_accolta,
                id_localita,
                giudice,
                riferimento_normativo,
                risarcimento,
                data_active,
                data_da,
                data_a
            } = model.res_flow_dashboard;

            let TIPOLOGIA_DANNO: string = tipologia_danno != "" ? "Hai scelto di analizzare il tipo di danno  '" + tipologia_danno + "'.<br>" : "";
            useTemplate = useTemplate.replace('{{TIPOLOGIA_DANNO}}', TIPOLOGIA_DANNO);

            let TIPOLOGIA_DANNO_PATRIMONIALE: string = tipologia_danno_patrimoniale != "" ? "Ti sei quindi soffermato sulle controversie patrimoniali di natura " + tipologia_danno_patrimoniale + "<br>":"";
            let TIPOLOGIA_DANNO_NON_PATRIMONIALE: string = tipologia_danno_non_patrimoniale != "" ? "Ti sei quindi soffermato sulle controversie non patrimoniali di natura " + tipologia_danno_non_patrimoniale + "<br>":"";
            let DOMANDA_ACCOLTA: string = domanda_accolta != "" ? domanda_accolta == "Si"? "Hai individuato sentenze in cui la domanda di risarcimento è stata accolta": "la domanda di risarcimento non è stata accolta": "";
            useTemplate = useTemplate.replace('{{TIPOLOGIA_DANNO_PATRIMONIALE}}', TIPOLOGIA_DANNO_PATRIMONIALE).replace('{{TIPOLOGIA_DANNO_NON_PATRIMONIALE}}', TIPOLOGIA_DANNO_NON_PATRIMONIALE).replace('{{DOMANDA_ACCOLTA}}', DOMANDA_ACCOLTA)

            let NUMERO_DOCUMENTI: string = model.res_get_dashboard.totale_numero_sentenze.valore.toString() + (model.res_get_dashboard.totale_numero_sentenze.valore > 1? " documenti": " documento");
            let SODDISFANO: string = 'soddisfa';
            if (model.res_get_dashboard.totale_numero_sentenze.valore > 1) SODDISFANO = 'soddisfano';
            useTemplate = useTemplate.replace('{{SODDISFANO}}', SODDISFANO);
            let ESTREMI_SELEZIONATE: string = "";
            if (model.res_get_dashboard.totale_numero_sentenze.valore > model.res_get_dashboard.sentenze_paginate.length) ESTREMI_SELEZIONATE = ` dei ${model.res_get_dashboard.sentenze_paginate.length} selezionati `
            useTemplate = useTemplate.replace('{{NUMERO_DOCUMENTI}}', NUMERO_DOCUMENTI).replace('{{ESTREMI_SELEZIONATE}}', ESTREMI_SELEZIONATE);

            let PERCORSO_VALORI: string = '';
            if (risarcimento_richiesto_active === true ||  risarcimento_disposto_totale_active === true ) {
                let inMerito: string = "Hai optato per applicare i filtri relativi agli aspetti legati ai valori.<br> Sono stati selezionati i procedimenti in cui si è disposto in merito a:<br>";
                if (risarcimento_richiesto_active) {
                    PERCORSO_VALORI += inMerito + "- Risarcimento richiesto da " + risarcimento_richiesto_da + " a " + risarcimento_richiesto_a + ";<br>";
                    inMerito = "";
                }
                if (risarcimento_disposto_totale_active) {
                    PERCORSO_VALORI += inMerito + "- Risarcimento totale da " + risarcimento_disposto_totale_da + " a " + risarcimento_disposto_totale_a + ";<br>";
                    inMerito = "";
                }
                PERCORSO_VALORI += "<br>";
            }
            useTemplate = useTemplate.replace('{{PERCORSO_VALORI}}', PERCORSO_VALORI);

            let  DURATA_PROCEDIMENTO: string = ( durata_procedimento ||  durata_procedimento_notNull) ?
                `Sono stati selezionati i procedimenti in cui è possibile risalire alla durata del processo;<br>` : '';
            useTemplate = useTemplate.replace('{{DURATA_PROCEDIMENTO}}',  DURATA_PROCEDIMENTO);

            let FILTRI_ULTERIORI: string = '';
            let INNER_FILTRI_ULTERIORI: string = '';

            if (riferimento_normativo != '') {
                INNER_FILTRI_ULTERIORI += `&bull; riferimento normativo ${riferimento_normativo};<br>`;
            }
            if (risarcimento) {
                INNER_FILTRI_ULTERIORI += `&bull; risarcimento ${risarcimento};<br>`;
            }
            if (data_active) {
                INNER_FILTRI_ULTERIORI += `&bull; data da ${new Date(data_da).toLocaleDateString()} a ${new Date(data_a).toLocaleDateString()};<br>`;
            }

            if (id_localita != '') {
                let nomeCitta: string = id_localita;
                let findCitta: KeyValoreStr | undefined = model.res_lista_citta.find((x: KeyValoreStr): boolean => {
                    return x.id_localita == id_localita
                });
                if (findCitta) nomeCitta = findCitta.citta;
                INNER_FILTRI_ULTERIORI += `&bull; il luogo di pronuncia ${nomeCitta};<br>`;
            }
            if (giudice != '') {
                INNER_FILTRI_ULTERIORI += `&bull; il Giudice della decisione ${giudice};<br>`;
            }
            if (INNER_FILTRI_ULTERIORI != "") {
                FILTRI_ULTERIORI += "<br>Sono stati applicati ulteriori filtri, quali:<br>";
                FILTRI_ULTERIORI += INNER_FILTRI_ULTERIORI;
                FILTRI_ULTERIORI += "<br>";
            }
            useTemplate = useTemplate.replace('{{FILTRI_ULTERIORI}}', FILTRI_ULTERIORI);

            let varLogo: string = "/img/logo.png";
            if (frontendConfig.rebranding) {
                varLogo = "/img/LOGO_STRATEGIA.png";
            }
            
            PdfGenerator.getImage(varLogo).then((image: HTMLImageElement): void => {
                let divHtmlToPdf: HTMLElement = document.createElement("div");
                divHtmlToPdf.style.width = "720px";
                divHtmlToPdf.style.maxWidth = "720px";
                divHtmlToPdf.style.fontSize = "12px";
                divHtmlToPdf.style.fontFamily = "Arial";
                divHtmlToPdf.style.letterSpacing = "0.05em";
                image.style.height = "50px";
                image.style.width = "auto";
                image.style.float = "left";
                image.style.marginRight = "20px";
                divHtmlToPdf.append(image)
                let contentDiv: HTMLElement = document.createElement("div");
                divHtmlToPdf.append(contentDiv)
                contentDiv.innerHTML = '<div style="font-size: 18px; color:var(--gfl-blue); padding: 27px 0 10px 70px">Report del ' + now.toLocaleDateString() + ' ' + now.toLocaleTimeString() + '</div><div><br></div><div>' + useTemplate +
                    '</div><div>' + PdfGenerator.getEstremi(useData.sentenze_paginate) + '</div><div><br></div><div style="font-size: 9px; color:#999999">' + PdfGenerator.templateFooter + '</div>';
                var doc = new jsPDF({
                        orientation: 'p',
                        unit: 'px',
                        format: 'a4',
                        putOnlyUsedFonts: true,
                        floatPrecision: 16,
                        hotfixes: ["px_scaling"]
                    }
                );
                doc.html(divHtmlToPdf, {
                    callback: function (doc) {
                        doc.save(fileName);
                        model.generatingPdf = false;
                    },
                    x: 30,
                    y: 30
                });
            });
        } else {
            alert("Prego attendere!")
        }
    }
    static res_generatePdfPercorso(what: string = "responsabilita") {
        let model: Model = Model.getReactiveInstance();
        if (!model.generatingPdf) {
            let now: Date = (new Date());
            model.generatingPdf = true;
            let unique: string = now.toISOString().replace(/ /g, "").replace(/:/g, "-");
            let where: string = "";
            let useFlow: ResponsabilitaPercorsoFlow;
            let useData: ResponsabilitaData;
            let useTemplate: string = PdfGenerator.templateResponsabilita_Comune;
            switch (what) {
                case "responsabilita":
                    useFlow = model.res_flow_percorso;
                    useData = model.res_get_percorso;
                    switch (useFlow.tipologia_danno) {
                        case "Patrimoniale":
                            where = "_patrimoniale";
                            break;
                        case "Non patrimoniale":
                            where = "_non_patrimoniale";
                            break;
                        case "Entrambi":
                            where = "_entrambi";
                            break;
                        }
                    break;
            }
            let fileName: string = "strategia_" + what + where + "_" + unique + ".pdf";

            //---
            const {
                tipologia_danno,
                tipologia_danno_patrimoniale,
                domanda_accolta,
                tipologia_danno_non_patrimoniale,
                valori,
                risarcimento_richiesto_active,
                risarcimento_richiesto_da,
                risarcimento_richiesto_a,
                risarcimento_disposto_totale_active,
                risarcimento_disposto_totale_da,
                risarcimento_disposto_totale_a
            } = model.res_flow_percorso;

            let TIPOLOGIA_DANNO: string = tipologia_danno != "" ? "Hai scelto di analizzare il tipo di danno  '" + tipologia_danno + "'.<br>" : "";
            useTemplate = useTemplate.replace('{{TIPOLOGIA_DANNO}}', TIPOLOGIA_DANNO);

            let TIPOLOGIA_DANNO_PATRIMONIALE: string = tipologia_danno_patrimoniale != "" ? "Ti sei quindi soffermato sulle controversie patrimoniali di natura " + tipologia_danno_patrimoniale + "<br>":"";
            let TIPOLOGIA_DANNO_NON_PATRIMONIALE: string = tipologia_danno_non_patrimoniale != "" ? "Ti sei quindi soffermato sulle controversie non patrimoniali di natura " + tipologia_danno_non_patrimoniale + "<br>":"";
            let DOMANDA_ACCOLTA: string = domanda_accolta != "" ? domanda_accolta == "Si"? "Hai individuato sentenze in cui la domanda di risarcimento è stata accolta": "la domanda di risarcimento non è stata accolta": "";
            useTemplate = useTemplate.replace('{{TIPOLOGIA_DANNO_PATRIMONIALE}}', TIPOLOGIA_DANNO_PATRIMONIALE).replace('{{TIPOLOGIA_DANNO_NON_PATRIMONIALE}}', TIPOLOGIA_DANNO_NON_PATRIMONIALE).replace('{{DOMANDA_ACCOLTA}}', DOMANDA_ACCOLTA)

            let NUMERO_DOCUMENTI: string = model.res_get_percorso.totale_numero_sentenze.valore.toString() + (model.res_get_percorso.totale_numero_sentenze.valore > 1? " documenti": " documento");
            let SODDISFANO: string = 'soddisfa';
            if (model.res_get_percorso.totale_numero_sentenze.valore > 1) SODDISFANO = 'soddisfano';
            useTemplate = useTemplate.replace('{{SODDISFANO}}', SODDISFANO);
            let ESTREMI_SELEZIONATE: string = "";
            if (model.res_get_percorso.totale_numero_sentenze.valore > model.res_get_percorso.sentenze_paginate.length) ESTREMI_SELEZIONATE = ` dei ${model.res_get_percorso.sentenze_paginate.length} selezionati `
            useTemplate = useTemplate.replace('{{NUMERO_DOCUMENTI}}', NUMERO_DOCUMENTI).replace('{{ESTREMI_SELEZIONATE}}', ESTREMI_SELEZIONATE);

            let PERCORSO_VALORI: string = '';
            if (valori || risarcimento_richiesto_active === true ||  risarcimento_disposto_totale_active === true ) {
                let inMerito: string = "Hai optato per applicare i filtri relativi agli aspetti legati ai valori.<br> Sono stati selezionati i procedimenti in cui si è disposto in merito a:<br>";
                if (risarcimento_richiesto_active) {
                    PERCORSO_VALORI += inMerito + "- Risarcimento richiesto da " + risarcimento_richiesto_da + " a " + risarcimento_richiesto_a + ";<br>";
                    inMerito = "";
                }
                if (risarcimento_disposto_totale_active) {
                    PERCORSO_VALORI += inMerito + "- Risarcimento totale da " + risarcimento_disposto_totale_da + " a " + risarcimento_disposto_totale_a + ";<br>";
                    inMerito = "";
                }
                PERCORSO_VALORI += "<br>";
            }
            useTemplate = useTemplate.replace('{{PERCORSO_VALORI}}', PERCORSO_VALORI);

            let  DURATA_PROCEDIMENTO: string = '';
            useTemplate = useTemplate.replace('{{DURATA_PROCEDIMENTO}}',  DURATA_PROCEDIMENTO);

            let FILTRI_ULTERIORI: string = '';
            useTemplate = useTemplate.replace('{{FILTRI_ULTERIORI}}', FILTRI_ULTERIORI);

            let varLogo: string = "/img/logo.png";
            if (frontendConfig.rebranding) {
                varLogo = "/img/LOGO_STRATEGIA.png";
            }
            
            PdfGenerator.getImage(varLogo).then((image: HTMLImageElement): void => {
                let divHtmlToPdf: HTMLElement = document.createElement("div");
                divHtmlToPdf.style.width = "720px";
                divHtmlToPdf.style.maxWidth = "720px";
                divHtmlToPdf.style.fontSize = "12px";
                divHtmlToPdf.style.fontFamily = "Arial";
                divHtmlToPdf.style.letterSpacing = "0.05em";
                image.style.height = "50px";
                image.style.width = "auto";
                image.style.float = "left";
                image.style.marginRight = "20px";
                divHtmlToPdf.append(image)
                let contentDiv: HTMLElement = document.createElement("div");
                divHtmlToPdf.append(contentDiv)
                contentDiv.innerHTML = '<div style="font-size: 18px; color:var(--gfl-blue); padding: 27px 0 10px 70px">Report del ' + now.toLocaleDateString() + ' ' + now.toLocaleTimeString() + '</div><div><br></div><div>' + useTemplate +
                    '</div><div>' + PdfGenerator.getEstremi(useData.sentenze_paginate) + '</div><div><br></div><div style="font-size: 9px; color:#999999">' + PdfGenerator.templateFooter + '</div>';
                var doc = new jsPDF({
                        orientation: 'p',
                        unit: 'px',
                        format: 'a4',
                        putOnlyUsedFonts: true,
                        floatPrecision: 16,
                        hotfixes: ["px_scaling"]
                    }
                );
                doc.html(divHtmlToPdf, {
                    callback: function (doc) {
                        doc.save(fileName);
                        model.generatingPdf = false;
                    },
                    x: 30,
                    y: 30
                });
            });
        } else {
            alert("Prego attendere!")
        }
    }

    //utils + docs
    static getImage(imagePath: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            if (!imagePath) {
                return reject();
            }
            let img: HTMLImageElement = new Image();
            img.width = 400;
            img.addEventListener('load', () => {
                resolve(img);
            }, false);
            img.src = imagePath;
        });
    }
    static getEstremi(sentenze_paginate: KeyValoreStrNum[]): string {
        let out: string = "";
        let ourArr: string[] = [];
        sentenze_paginate.forEach((item: KeyValoreStrNum) => {
            if (item.estremi) {
                ourArr.push("<li>" + item.estremi + "</li>");
            }
        })
        if (ourArr.length > 0) {
            out = "<ul>" + ourArr.join(" ") + "</ul>";
        }
        return out;
    }
}
